/**
 * Overlay user-authored Coverage Matrix structured fields onto SED
 * variable paths using REQUIREMENT_FIELD_ALIGNMENT + reverse projection.
 */
import type { ExtractedClaim } from '../../types/siteContext';
import { detachElectricityPriceContext } from './electricityPriceAdoption';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { FieldPath } from '../evidenceLineage/requirementToFields';
import { collectSedPatchesForClaim } from './authoredSchemaToSed';

export interface AuthoredSedBinding {
  fieldPath: FieldPath;
  variablePath: string;
  value: unknown;
  claimId: string;
}

export function fieldPathToVariablePath(fieldPath: FieldPath): string {
  const dot = fieldPath.indexOf('.');
  if (dot === -1) return fieldPath;
  const section = fieldPath.slice(0, dot);
  const rest = fieldPath.slice(dot + 1);
  return `${section}.data.${rest}`;
}

/** Latest user-typed structured value per SED variable path. */
export function indexAuthoredStructuredFields(
  claims: readonly ExtractedClaim[],
): Map<string, AuthoredSedBinding> {
  const sorted = [...claims]
    .filter(c => c.source === 'user-typed' && c.requirementId && c.structuredFields)
    .sort((a, b) => (Date.parse(b.lastWrittenAt ?? '') || 0) - (Date.parse(a.lastWrittenAt ?? '') || 0));

  const index = new Map<string, AuthoredSedBinding>();
  for (const claim of sorted) {
    for (const patch of collectSedPatchesForClaim(claim)) {
      const variablePath = fieldPathToVariablePath(patch.fieldPath);
      if (index.has(variablePath)) continue;
      index.set(variablePath, {
        fieldPath: patch.fieldPath,
        variablePath,
        value: patch.value,
        claimId: claim.id,
      });
    }
  }
  return index;
}

/** Patch live SED section payloads from authored structured fields. */
export function applyAuthoredStructuredFieldsToSed(
  sed: SiteEvidenceDocument,
  claims: readonly ExtractedClaim[],
): number {
  const index = indexAuthoredStructuredFields(claims);
  let patched = 0;
  for (const binding of index.values()) {
    const parts = binding.variablePath.split('.');
    const sectionId = parts[0];
    const key = parts[parts.length - 1];
    const slots = sed as unknown as Record<string, {
      data?: Record<string, unknown>;
      status?: string;
      confidence?: number;
      authoredClaimIds?: string[];
      source?: string;
      provenanceDetail?: SiteEvidenceDocument['electricity']['provenanceDetail'];
    } | undefined>;
    const section = slots[sectionId];
    if (!section) continue;
    if (!section.data || typeof section.data !== 'object') {
      section.data = {};
    }
    const wasUnavailable = section.status === 'unavailable';
    const priceAdoption = sectionId === 'electricity' && key === 'retailRate'
      && typeof binding.value === 'number' && Number.isFinite(binding.value);
    const priorValue = section.data[key];
    const priorData = priceAdoption ? { ...section.data } : undefined;
    const electricityPriceContext = priceAdoption
      ? detachElectricityPriceContext(priorData, section.data, section.source ?? '', binding.value) : undefined;
    // Numeric equality does not mean the provider supplied the user's price.
    // Capture the authored binding once, including an equal-valued adoption.
    const newPriceBinding = priceAdoption && !(section.authoredClaimIds ?? []).includes(binding.claimId);
    if (section.data[key] === binding.value && !electricityPriceContext && !newPriceBinding) continue;
    section.data[key] = binding.value;
    if (priceAdoption) {
      section.status = 'verified-from-context';
      section.provenanceDetail = {
        ...section.provenanceDetail, mergeReason: 'authored-price-adoption',
        docValue: binding.value,
        ...(electricityPriceContext ? { electricityPriceContext,
          ...(priorValue !== undefined ? { providerValue: priorValue } : {}) } : {}),
      };
    }
    if (wasUnavailable) {
      section.status = 'verified-from-context';
      section.confidence = Math.max(section.confidence ?? 0, 0.75);
    }
    section.authoredClaimIds = Array.from(new Set([...(section.authoredClaimIds ?? []), binding.claimId]));
    patched += 1;
  }
  return patched;
}

/** Resolve bindsToSedPaths for an authored claim (lineage + browser indexing). */
export function bindsToSedPathsForAuthoredClaim(
  claim: Pick<ExtractedClaim, 'requirementId' | 'structuredFields' | 'source'>,
): string[] {
  if (claim.source !== 'user-typed') return [];
  const full = {
    id: 'preview',
    category: 'general' as const,
    sourceSpan: '',
    claim: '',
    extractorConfidence: 0,
    source: 'user-typed' as const,
    requirementId: claim.requirementId,
    structuredFields: claim.structuredFields,
  };
  if (!full.requirementId || !full.structuredFields) return [];
  return collectSedPatchesForClaim(full).map(p => fieldPathToVariablePath(p.fieldPath));
}

export function authoredValueForVariablePath(
  claims: readonly ExtractedClaim[],
  variablePath: string,
): AuthoredSedBinding | undefined {
  return indexAuthoredStructuredFields(claims).get(variablePath);
}
